import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAiModels } from "../../api/services/aiService";
import { AiUsageMini, useAiUsage } from "../../components/ai/AiUsage";
import { useWorkspace } from "../../integrationQA/context/WorkspaceContext";

/**
 * OpenAI configuration presentation. ChatBizz runs the AI account, so there is nothing to connect or paste here:
 * the card shows who provides the AI, which models this plan includes and how much of the monthly quota is used.
 */
export default function OpenAiConfiguration() {
  const { can } = useWorkspace();
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");
  const { usage } = useAiUsage({ enabled: can("billing:read"), pollMs: 30000 });

  useEffect(() => {
    let active = true;
    getAiModels().then((c) => { if (active) setCatalog(c); }).catch((e) => { if (active) setError(e.message || "Could not load AI models."); });
    return () => { active = false; };
  }, []);

  return (
    <section className="card p-4 mb-4 openai-config" aria-labelledby="openai-title">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div className="d-flex align-items-center gap-3">
          <span className="integration-icon" aria-hidden="true"><i className="bi bi-stars" /></span>
          <div><h3 className="h5 mb-0" id="openai-title">OpenAI</h3><div className="text-muted small">AI provider for bot replies</div></div>
        </div>
        <span className="badge text-bg-success">Managed by ChatBizz</span>
      </div>
      <p className="text-muted small mb-3">There is no API key to enter. ChatBizz provides the AI and includes it in your plan, so replies work as soon as a bot is published.</p>

      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {!catalog && !error && <p className="text-muted small" role="status">Loading models…</p>}
      {catalog && (
        <>
          <div className="small text-muted mb-2">Models on the <strong>{catalog.plan}</strong> plan</div>
          <ul className="list-unstyled d-grid gap-2 mb-3">
            {catalog.models.map((m) => (
              <li key={m.id} className="d-flex justify-content-between gap-3 border rounded p-2">
                <div><strong>{m.label}</strong>{m.id === catalog.defaultModel && <span className="text-primary small"> · recommended</span>}<div className="small text-muted">{m.description}</div></div>
                <span className={`badge align-self-start ${m.available ? "text-bg-success" : "text-bg-secondary"}`}>{m.available ? "Included" : `${m.minPlan} plan`}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {can("billing:read") && <AiUsageMini usage={usage} />}
      <div className="d-flex flex-wrap gap-2 mt-3">
        {can("bots:manage") && <Link className="btn btn-primary btn-sm" to="/bot-configuration?section=model">Choose a model for a bot</Link>}
        {can("billing:read") && <Link className="btn btn-outline-secondary btn-sm" to="/ai-usage">View AI usage</Link>}
      </div>
    </section>
  );
}
